import {
  LargeDropdown,
  LargeDropdownCaret,
  LargeDropdownCurrentSelection,
  LargeDropdownOption,
  LargeDropdownSelect,
  Dropdown,
  DropdownCaret,
  DropdownCurrentSelection,
  DropdownOption,
  DropdownSelect,
  ValidationContainer,
  ValidationMessage,
} from "@tandemdiabetes/sugarmate-react-components"
import type { ChangeEventHandler } from "react"
import {
  useCallback,
  useMemo,
  useRef,
} from "react"

import type { Country } from "react-phone-number-input"
import {
  getCountryCallingCode,
  isSupportedCountry,
} from "react-phone-number-input"
import { usePolyglot } from "src/contexts"
import { useCountries } from "src/hooks"

export const defaultCountryCode = ""

interface Props {
  large?: boolean;
  required?: boolean;
  countryCode: string;
  setCountryCode: (value: string) => void;
}

interface Option {
  value: string;
  children: string;
  disabled?: boolean;
}

const id = "country-code-select"
const polyglotPrefix = "components.country_code_select."

const countryCodeSelectErrorId = "country-code-select-error"

export function CountryCodeSelect(props: Props) {
  const {
    large,
    required,
    countryCode,
    setCountryCode,
  } = props

  const polyglot = usePolyglot()
  const countries = useCountries().data
  const selectRef = useRef<HTMLSelectElement>(null)

  const options: Array<Option> = useMemo(
    () => {
      const optionsArray: Array<Option> = [
        // the placeholder option
        {
          value: defaultCountryCode,
          children: polyglot.t(`${polyglotPrefix}placeholder`),
          disabled: true,
        },
      ]

      const countryOptions = (countries ?? [])
        .filter(
          (country): boolean => {
            if (!isSupportedCountry(country.country_code)) {
              return false
            }

            return country.phone_supported
          },
        )
        .map(
          (country): Option => ({
            value: country.country_code,
            children: polyglot.t(`country.${country.country_code}`),
          }),
        )
        .sort(
          (optionA, optionB) => optionA.children.localeCompare(optionB.children),
        )
        .map(
          (option: Option) => {
            if (!option.value) {
              return option
            }

            const callingCode = getCountryCallingCode(option.value as Country)

            return {
              ...option,
              children: `+${callingCode} ${option.children}`,
            }
          },
        )

      optionsArray.push(...countryOptions)
      return optionsArray
    },
    [
      polyglot,
      countries,
    ],
  )

  const onChange: ChangeEventHandler<HTMLSelectElement> = useCallback(
    (e) => {
      const newCountryCode = e.target.value
      const country = (countries ?? []).find((c) => c.country_code === newCountryCode)

      if (country?.phone_supported) {
        selectRef.current?.setCustomValidity("")
      } else {
        selectRef.current?.setCustomValidity(polyglot.t(`${polyglotPrefix}unsupported_error`))
      }

      setCountryCode(newCountryCode)
    },
    [
      polyglot,
      countries,
      selectRef,
      setCountryCode,
    ],
  )

  const DropdownComponent = large ? LargeDropdown : Dropdown
  const DropdownSelectComponent = large ? LargeDropdownSelect : DropdownSelect
  const DropdownOptionComponent = large ? LargeDropdownOption : DropdownOption
  const DropdownCurrentSelectionComponent = large ? LargeDropdownCurrentSelection : DropdownCurrentSelection
  const DropdownCaretComponent = large ? LargeDropdownCaret : DropdownCaret

  return (
    <ValidationContainer>
      <DropdownComponent>
        <DropdownSelectComponent
          id={id}
          value={countryCode}
          onChange={onChange}
          ref={selectRef}
          autoComplete="country"
          required={required}
        >
          {options.map(
            (selectOption) => (
              <DropdownOptionComponent
                key={selectOption.value}
                {...selectOption}
              />
            ),
          )}
        </DropdownSelectComponent>
        <DropdownCurrentSelectionComponent />
        <DropdownCaretComponent />
      </DropdownComponent>
      <ValidationMessage
        id={countryCodeSelectErrorId}
        invalidMessage={{
          customError: polyglot.t(`${polyglotPrefix}unsupported_error`),
          valueMissing: polyglot.t("validation_message.value_missing"),
        }}
      />
    </ValidationContainer>
  )
}
